// src/components/TopNav.js
import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import logo from '../assets/logo.png';

/**
 * TopNav - Desktop navigation bar with hamburger menu
 *
 * Features:
 * - Logo and branding
 * - Hamburger menu for navigation links
 * - User profile dropdown
 * - Login/Register buttons (when not authenticated)
 * - Role-based navigation items
 * - Hidden on mobile (< 1024px) - BottomNav is used instead
 */
const TopNav = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const handleLogout = async () => {
    setProfileOpen(false);
    // A kiosk leaves kiosk mode from its profile screen, with the kiosk password.
    if (user?.role === 'kiosk') {
      navigate('/kiosk/profile');
      return;
    }
    await logout();
    navigate('/');
  };

  // Determine navigation items based on user role
  const getNavItems = () => {
    if (!user) {
      return [
        { label: 'Home', path: '/' },
        { label: 'Vehicles', path: '/vehicles' },
        { label: 'Login', path: '/login' },
        { label: 'Sign Up', path: '/register' },
      ];
    }

    // Dealer users don't need My Bookings and Favorites - they use Dealer Dashboard
    if (user.role === 'dealer_admin' || user.role === 'dealer_staff') {
      return [
        { label: 'Vehicles', path: '/vehicles' },
        { label: 'Dealer Dashboard', path: '/dealer' },
      ];
    }

    const baseItems = [
      { label: 'Vehicles', path: '/vehicles' },
      { label: 'My Bookings', path: '/bookings' },
      { label: 'Favorites', path: '/favorites' },
    ];

    // Add role-specific items
    if (user.role === 'admin') {
      baseItems.push({ label: 'Admin Panel', path: '/admin/dealers' });
    } else if (user.role === 'kiosk') {
      return [
        { label: 'Kiosk Home', path: '/kiosk' },
        { label: 'Book', path: '/kiosk/book' },
        { label: 'Search', path: '/kiosk/search' },
      ];
    }

    return baseItems;
  };

  const navItems = getNavItems();

  return (
    <nav className="hidden lg:flex items-center justify-between px-6 py-4 bg-app-bg-secondary-light/80 dark:bg-black/80 backdrop-blur border-b border-app-border-light dark:border-app-border sticky top-0 z-50">
      {/* Logo */}
      <Link to="/" className="flex items-center p-3">
        <img
          src={logo}
          alt="SelfDrive"
          className="h-14 min-w-[45px] w-auto"
          style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))' }}
        />
      </Link>

      {/* Desktop Navigation Links */}
      <div className="flex items-center space-x-6">
        {/* Hamburger Menu */}
        <div className="relative">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-2 rounded-lg hover:bg-app-bg-tertiary-light dark:hover:bg-app-bg-tertiary transition-colors"
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6 text-app-text-light dark:text-app-text"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {menuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>

          {/* Dropdown Menu */}
          {menuOpen && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-40"
                onClick={() => setMenuOpen(false)}
              />

              {/* Menu Items */}
              <div className="absolute right-0 mt-2 w-56 bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-lg shadow-xl border border-app-border-light dark:border-app-border py-2 z-50">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-app-text-light dark:text-app-text hover:bg-app-bg-tertiary-light dark:hover:bg-app-bg-tertiary transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>

        {/* User Profile or Auth Buttons */}
        {user ? (
          <div className="relative">
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-app-bg-tertiary-light dark:hover:bg-app-bg-tertiary transition-colors"
            >
              <div className="w-8 h-8 bg-app-accent rounded-full flex items-center justify-center text-white text-sm font-semibold">
                {user.name ? user.name[0].toUpperCase() : 'U'}
              </div>
              <span className="text-sm text-app-text-light dark:text-app-text">{user.name || user.email}</span>
              <svg
                className={`w-4 h-4 text-app-text-light dark:text-app-text transition-transform ${
                  profileOpen ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {/* Profile Dropdown */}
            {profileOpen && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setProfileOpen(false)}
                />

                {/* Dropdown Items */}
                <div className="absolute right-0 mt-2 w-48 bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-lg shadow-xl border border-app-border-light dark:border-app-border py-2 z-50">
                  <Link
                    to="/profile"
                    onClick={() => setProfileOpen(false)}
                    className="block px-4 py-2 text-sm text-app-text-light dark:text-app-text hover:bg-app-bg-tertiary-light dark:hover:bg-app-bg-tertiary transition-colors"
                  >
                    Profile
                  </Link>
                  <div className="border-t border-app-border-light dark:border-app-border my-1"></div>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-app-error hover:bg-app-bg-tertiary-light dark:hover:bg-app-bg-tertiary transition-colors"
                  >
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="flex items-center space-x-3">
            <Link
              to="/login"
              className="px-4 py-2 text-sm text-app-text hover:text-app-accent transition-colors"
            >
              Login
            </Link>
            <Link
              to="/register"
              className="px-4 py-2 text-sm bg-app-accent text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Sign Up
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default TopNav;
